import { observer } from 'mobx-react-lite';
import { Box, getVariant } from '@apia/theme';
import { ChatMessage } from './ChatMessage';
import { Message } from './Message';
import { Virtuoso } from 'react-virtuoso';
import type { Components, VirtuosoHandle } from 'react-virtuoso';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type {
  HTMLAttributes,
  WheelEvent as ReactWheelEvent,
} from 'react';

const CHAT_HISTORY_CONTENT_CHANGED_EVENT = 'chatHistoryContentChanged';

type THistoryContext = {
  bottomInset: number;
};

function getMessageGroupClass(messages: ChatMessage[], index: number) {
  const current = messages[index];
  const classes: string[] = [];

  if (messages[index - 1]?.messageType !== current.messageType) {
    classes.push('first');
  }

  if (messages[index + 1]?.messageType !== current.messageType) {
    classes.push('last');
  }

  return classes.length > 0 ? classes.join(' ') : undefined;
}

const VirtualHistoryList = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className = '', ...props }, ref) => (
  <Box
    {...props}
    ref={ref}
    {...getVariant('layout.common.chat.history')}
    className={`chat_history ${className}`}
  />
));

VirtualHistoryList.displayName = 'VirtualHistoryList';

const VirtualHistoryItem = ({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={`chatHistoryVirtualItem ${className}`}>
    {children}
  </div>
);

const VirtualHistoryFooter = ({ context }: { context?: THistoryContext }) => (
  <div
    className="chatHistoryVirtualFooter"
    style={{ height: context?.bottomInset ?? 0 }}
  />
);

const virtualHistoryComponents: Components<ChatMessage, THistoryContext> = {
  Footer: VirtualHistoryFooter,
  Item: VirtualHistoryItem,
  List: VirtualHistoryList,
};

const HistoryMessage = observer(
  ({
    current,
    index,
    messages,
  }: {
    current: ChatMessage;
    index: number;
    messages: ChatMessage[];
  }) => (
    <Message
      className={getMessageGroupClass(messages, index)}
      message={current}
    />
  ),
);

export const History = observer(
  ({
    bottomInset = 0,
    messages,
    className,
  }: {
    bottomInset?: number;
    messages: ChatMessage[];
    className?: string;
  }) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const messageCountRef = useRef(messages.length);
    const autoScrollEnabledRef = useRef(true);
    const allowAutoScrollRestoreRef = useRef(false);
    const pendingScrollFrameRef = useRef(0);
    messageCountRef.current = messages.length;

    const scrollToBottom = useCallback(() => {
      if (pendingScrollFrameRef.current) {
        cancelAnimationFrame(pendingScrollFrameRef.current);
      }

      pendingScrollFrameRef.current = requestAnimationFrame(() => {
        const scroll = () => {
          const lastIndex = messageCountRef.current - 1;
          if (lastIndex >= 0) {
            virtuosoRef.current?.scrollToIndex({
              align: 'end',
              behavior: 'auto',
              index: lastIndex,
            });
          }
        };

        scroll();
        pendingScrollFrameRef.current = requestAnimationFrame(() => {
          scroll();
          pendingScrollFrameRef.current = 0;
        });
      });
    }, []);

    useEffect(() => {
      const enableAutoScroll = () => {
        autoScrollEnabledRef.current = true;
        allowAutoScrollRestoreRef.current = false;
        scrollToBottom();
      };
      const keepAutoScroll = () => {
        if (autoScrollEnabledRef.current) {
          scrollToBottom();
        }
      };

      document.addEventListener('enableAutoScroll', enableAutoScroll);
      document.addEventListener(
        CHAT_HISTORY_CONTENT_CHANGED_EVENT,
        keepAutoScroll,
      );

      return () => {
        document.removeEventListener('enableAutoScroll', enableAutoScroll);
        document.removeEventListener(
          CHAT_HISTORY_CONTENT_CHANGED_EVENT,
          keepAutoScroll,
        );
        if (pendingScrollFrameRef.current) {
          cancelAnimationFrame(pendingScrollFrameRef.current);
        }
      };
    }, [scrollToBottom]);

    useEffect(() => {
      if (autoScrollEnabledRef.current) {
        scrollToBottom();
      }
    }, [bottomInset, messages.length, scrollToBottom]);

    const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
      if (atBottom && allowAutoScrollRestoreRef.current) {
        autoScrollEnabledRef.current = true;
        allowAutoScrollRestoreRef.current = false;
      }
    }, []);

    const handleWheel = useCallback((ev: ReactWheelEvent<HTMLDivElement>) => {
      if (ev.deltaY < 0) {
        autoScrollEnabledRef.current = false;
        allowAutoScrollRestoreRef.current = false;
      } else if (ev.deltaY > 0) {
        allowAutoScrollRestoreRef.current = true;
      }
    }, []);

    const handleTouchMove = useCallback(() => {
      autoScrollEnabledRef.current = false;
    }, []);

    const handleTotalListHeightChanged = useCallback(() => {
      if (autoScrollEnabledRef.current) {
        scrollToBottom();
      }
    }, [scrollToBottom]);

    const historyClassName = useMemo(
      () => `chatHistoryVirtualScroller ${className || ''}`,
      [className],
    );

    return (
      <Virtuoso
        alignToBottom
        atBottomStateChange={handleAtBottomStateChange}
        atBottomThreshold={150}
        className={historyClassName}
        context={{ bottomInset }}
        components={virtualHistoryComponents}
        computeItemKey={(_, current) => current.id}
        data={messages}
        followOutput={() => (autoScrollEnabledRef.current ? 'auto' : false)}
        increaseViewportBy={600}
        initialTopMostItemIndex={
          messages.length > 0 ? messages.length - 1 : undefined
        }
        itemContent={(i, current) => (
          <HistoryMessage current={current} index={i} messages={messages} />
        )}
        onTouchMove={handleTouchMove}
        onWheel={handleWheel}
        ref={virtuosoRef}
        style={{ flex: '1 1 auto', height: '100%', minHeight: 0 }}
        totalListHeightChanged={handleTotalListHeightChanged}
      />
    );
  },
);
